"use client";

import type React from "react";
import { useState, useEffect, useCallback } from "react";
import {
  Box,
  FormControl,
  FormLabel,
  Switch,
  Text,
  VStack,
  useToast,
  HStack,
  Radio,
  RadioGroup,
  Textarea,
  Slider,
  SliderTrack,
  SliderFilledTrack,
  SliderThumb,
} from "@chakra-ui/react";
import { useAppTheme } from "../../../ThemeContext.tsx";
import { agentService } from "../../../services/agentService.ts";

interface VoicemailSettingsProps {
  agentId: string;
  initialSettings?: {
    enable_voicemail_detection?: boolean;
    voicemail_message?: string;
    voicemail_detection_timeout_ms?: number;
  };
}

export const VoicemailSettings: React.FC<VoicemailSettingsProps> = ({
  agentId,
  initialSettings,
}) => {
  const [settings, setSettings] = useState(initialSettings || {});
  const [voicemailAction, setVoicemailAction] = useState<"hangup" | "message">(
    settings.voicemail_message ? "message" : "hangup"
  );
  const [isUpdating, setIsUpdating] = useState(false);

  const toast = useToast();
  const { resolvedTheme } = useAppTheme();
  const isDark = resolvedTheme === "dark";

  useEffect(() => {
    setSettings(initialSettings || {});
    setVoicemailAction(
      initialSettings?.voicemail_message ? "message" : "hangup"
    );
  }, [initialSettings]);

  const updateSettings = useCallback(
    async (newSettings: Partial<typeof settings>) => {
      setIsUpdating(true);
      try {
        await agentService.updateAgentSettings(agentId, newSettings);

        setSettings((prevSettings) => ({ ...prevSettings, ...newSettings }));
        toast({
          title: "Voicemail settings updated",
          status: "success",
          duration: 2000,
        });
      } catch (error: any) {
        console.error("Error updating voicemail settings:", error);
        toast({
          title: "Error updating voicemail settings",
          description:
            error.response?.data?.message || "An unexpected error occurred",
          status: "error",
          duration: 3000,
        });
      } finally {
        setIsUpdating(false);
      }
    },
    [agentId, toast]
  );

  const handleVoicemailDetectionChange = async (checked: boolean) => {
    await updateSettings({
      enable_voicemail_detection: checked,
      voicemail_message: checked
        ? voicemailAction === "message"
          ? settings.voicemail_message
          : ""
        : "",
    });
  };

  const handleVoicemailActionChange = async (value: "hangup" | "message") => {
    setVoicemailAction(value);
    if (value === "hangup") {
      await updateSettings({ voicemail_message: "" });
    }
  };

  const handleVoicemailMessageChange = (value: string) => {
    setSettings((prev) => ({ ...prev, voicemail_message: value }));
  };

  const handleVoicemailMessageBlur = async () => {
    if (settings.enable_voicemail_detection && voicemailAction === "message") {
      await updateSettings({ voicemail_message: settings.voicemail_message });
    }
  };

  const handleTimeoutChange = async (value: number) => {
    await updateSettings({ voicemail_detection_timeout_ms: value });
  };

  const formatDetectionTimeout = (ms: number) => {
    if (ms >= 60000) {
      return `${(ms / 60000).toFixed(1)} min`;
    }
    return `${Math.round(ms / 1000)} s`;
  };

  return (
    <FormControl>
      <HStack justify="space-between" align="flex-start">
        <Box>
          <FormLabel
            fontSize="sm"
            fontWeight="medium"
            color={isDark ? "gray.300" : "gray.700"}
            mb={1}
          >
            Voicemail Detection
          </FormLabel>
          <Text fontSize="xs" color={isDark ? "gray.400" : "gray.600"}>
            Hang up or leave a voicemail if a voicemail is detected
          </Text>
        </Box>
        <Switch
          isChecked={settings.enable_voicemail_detection}
          onChange={(e) => handleVoicemailDetectionChange(e.target.checked)}
          isDisabled={isUpdating}
          colorScheme={isDark ? "whiteAlpha" : "blackAlpha"}
          borderColor={isDark ? "gray.500" : "gray.400"}
          sx={{
            ".chakra-switch__track": {
              backgroundColor: settings.enable_voicemail_detection
                ? isDark
                  ? "gray.600"
                  : "gray.400" // Darker color when turned on
                : isDark
                ? "gray.700"
                : "gray.300", // Color when turned off
            },
          }}
        />
      </HStack>

      {settings.enable_voicemail_detection && (
        <VStack spacing={4} mt={4} align="stretch">
          <RadioGroup
            value={voicemailAction}
            onChange={handleVoicemailActionChange}
          >
            <VStack align="start" spacing={3}>
              <Radio
                value="hangup"
                colorScheme="gray"
                sx={{
                  borderWidth: "2px",
                  borderColor: isDark ? "gray.500" : "gray.400",
                  borderRadius: "full",
                  width: "15px",
                  height: "15px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  _checked: {
                    bg: isDark ? "gray.700" : "gray.200",
                    borderColor: isDark ? "white" : "black",
                    position: "relative",
                    _after: {
                      content: '""',
                      position: "absolute",
                      width: "7px",
                      height: "7px",
                      bg: "gray.500",
                      borderRadius: "full",
                    },
                  },
                }}
              >
                Hang up if reaching voicemail
              </Radio>
              <Radio
                value="message"
                colorScheme="gray"
                sx={{
                  borderWidth: "2px",
                  borderColor: isDark ? "gray.500" : "gray.400",
                  borderRadius: "full",
                  width: "15px",
                  height: "15px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  _checked: {
                    bg: isDark ? "gray.700" : "gray.200",
                    borderColor: isDark ? "white" : "black",
                    position: "relative",
                    _after: {
                      content: '""',
                      position: "absolute",
                      width: "7px",
                      height: "7px",
                      bg: "gray.500",
                      borderRadius: "full",
                    },
                  },
                }}
              >
                Leave a message if reaching voicemail
              </Radio>
            </VStack>
          </RadioGroup>

          {voicemailAction === "message" && (
            <Textarea
              sx={
                isDark
                  ? {
                      "&::-webkit-scrollbar": {
                        height: "8px",
                      },
                      "&::-webkit-scrollbar-track": {
                        backgroundColor: "#2D3748", // Dark track color
                      },
                      "&::-webkit-scrollbar-thumb": {
                        backgroundColor: "#4A5568", // Dark thumb color
                        borderRadius: "2px",
                      },
                      // For Firefox
                      scrollbarColor: "#4A5568 #2D3748", // thumb, then track
                      _hover: {
                        scrollbarColor: "#4A5568 #2D3748", // same colors on hover
                      },
                    }
                  : {
                      "&::-webkit-scrollbar": {
                        height: "4px",
                      },
                      "&::-webkit-scrollbar-button": {
                        display: "none",
                      },
                      "&::-webkit-scrollbar-track": {
                        backgroundColor: "#fff",
                      },
                      "&::-webkit-scrollbar-thumb": {
                        backgroundColor: "#fff",
                        borderRadius: "2px",
                      },
                      scrollbarColor: "rgb(220, 220, 220) white",
                      _hover: {
                        scrollbarColor: "rgb(220, 220, 220) white",
                      },
                    }
              }
              value={settings.voicemail_message}
              onChange={(e) => handleVoicemailMessageChange(e.target.value)}
              onBlur={handleVoicemailMessageBlur}
              placeholder="Message content, use {{}} to add variable"
              size="sm"
              _placeholder={{ color: isDark ? "gray.500" : "gray.400" }}
              resize="vertical"
              minH="80px"
              bg={isDark ? "gray.700" : "white"}
              borderColor={isDark ? "gray.600" : "gray.300"}
              _hover={{ borderColor: isDark ? "gray.500" : "gray.400" }}
              _focus={{
                borderColor: isDark ? "gray.500" : "gray.400",
                boxShadow: "none",
              }}
            />
          )}

          <Box>
            <Text
              fontSize="sm"
              fontWeight="medium"
              color={isDark ? "gray.300" : "gray.700"}
              mb={1}
            >
              Voicemail Detection Duration
            </Text>
            <Text fontSize="xs" color={isDark ? "gray.400" : "gray.600"} mb={2}>
              Duration for which voicemail detection will be active during the
              call
            </Text>
            <HStack spacing={4}>
              <Slider
                aria-label="Voicemail detection timeout"
                min={5000}
                max={180000}
                step={1000}
                value={settings.voicemail_detection_timeout_ms}
                onChange={(value) =>
                  setSettings((prev) => ({
                    ...prev,
                    voicemail_detection_timeout_ms: value,
                  }))
                }
                onChangeEnd={handleTimeoutChange}
                isDisabled={isUpdating}
                flex={1}
              >
                <SliderTrack bg={isDark ? "gray.600" : "gray.200"}>
                  <SliderFilledTrack bg="gray.500" />
                </SliderTrack>
                <SliderThumb />
              </Slider>
              <Text
                fontSize="sm"
                color={isDark ? "gray.300" : "gray.700"}
                minW="48px"
              >
                {formatDetectionTimeout(
                  settings.voicemail_detection_timeout_ms || 30000
                )}
              </Text>
            </HStack>
          </Box>
        </VStack>
      )}
    </FormControl>
  );
};
